import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Users, ArrowRight, Check, UserCheck, Clock, 
  BarChart3, Shield, Smartphone, Settings, Sparkles, MessageSquare
} from 'lucide-react';

const CrewManagementPage = () => {
  const features = [
    {
      icon: Users,
      title: "Create Crews",
      description: "Organize your workforce into crews. Assign a crew leader and add team members for easy route assignment."
    },
    {
      icon: UserCheck,
      title: "Route Assignment",
      description: "Assign entire routes to crews with one click. Crews see their full day's work in the mobile app."
    },
    {
      icon: Clock,
      title: "Time Tracking",
      description: "Track when crews start and complete each job. Build accurate time estimates for future scheduling."
    },
    {
      icon: BarChart3,
      title: "Productivity Reports",
      description: "See which crews are most productive. Track jobs completed, revenue generated, and efficiency metrics."
    },
    {
      icon: Shield,
      title: "Role-Based Access",
      description: "Give different team members different permissions. Admins see everything; crew members see their jobs."
    },
    {
      icon: Settings,
      title: "Crew Status",
      description: "Track which crews are active, on break, or off for the day. Know your workforce availability at a glance."
    }
  ];

  const roles = [
    { name: "Company Admin", description: "Full access to all features, settings, and reports", color: "bg-purple-100 text-purple-700" },
    { name: "Manager", description: "Manage schedules, crews, and view reports", color: "bg-blue-100 text-blue-700" },
    { name: "Office Staff", description: "Handle customers, estimates, and invoicing", color: "bg-emerald-100 text-emerald-700" },
    { name: "Crew Member", description: "View assigned jobs and complete work on mobile", color: "bg-amber-100 text-amber-700" }
  ];

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Users className="h-4 w-4" />
                Core Feature
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                Crew Management
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Keep your teams organized, assigned, and productive. Track performance, 
                manage permissions, and know exactly what's happening in the field.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-primary hover:bg-primary/90">
                    Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1734079692160-fcbe4be6ab96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBsYXduJTIwbW93aW5nJTIwbGFuZHNjYXBpbmclMjBzZXJ2aWNlfGVufDB8fHx8MTc3Mzc5NDY4NXww&ixlib=rb-4.1.0&q=85"
                alt="Landscaping crew working"
                className="w-full h-80 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Role Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Role-Based Permissions</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Give each team member access to exactly what they need—nothing more, nothing less.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {roles.map((role, index) => (
              <Card key={index} className="border-gray-100">
                <CardContent className="p-6">
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${role.color} mb-4`}>
                    {role.name}
                  </span>
                  <p className="text-gray-600 text-sm">{role.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Complete Crew Control</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From assignment to completion tracking, everything you need to manage your workforce.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-gray-100 bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Connection */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Connected to the Mobile App
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                When you assign crews to routes, they instantly see their work in the mobile app. 
                As they complete jobs, you see real-time updates in the office.
              </p>
              <ul className="space-y-4">
                {[
                  "Crews see their assigned routes on mobile",
                  "Real-time job status updates",
                  "Track actual vs estimated completion times",
                  "Photo uploads from the field",
                  "Notes and communication flow both ways"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/features/mobile">
                  <Button variant="outline">
                    Learn About Mobile App <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8 flex items-center justify-center">
              <div className="bg-white rounded-3xl shadow-xl p-4 w-64">
                <div className="bg-primary text-white rounded-t-2xl p-4 -mt-4 -mx-4 mb-4">
                  <p className="text-sm opacity-80">Today's Route</p>
                  <p className="font-semibold">Team Alpha</p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-sm">123 Oak St - Done</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    <span className="text-sm">456 Maple Ave - In Progress</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="text-sm">789 Pine Rd - Next</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Integration Section */}
      <section className="py-20 bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Assign Crews with a Single Command
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Our AI Assistant makes crew management effortless. Just tell it what you need, 
                and it handles assignments, checks availability, and updates schedules instantly.
              </p>
              <ul className="space-y-4">
                {[
                  "Assign crews to jobs by simply asking",
                  "Check crew availability instantly",
                  "Get productivity summaries on demand",
                  "Reassign jobs when plans change"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/features/ai-assistant">
                  <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50">
                    Learn About AI Assistant <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-xl border border-violet-100 overflow-hidden">
              <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-semibold text-white">AI Assistant</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-violet-50 rounded-lg p-4 ml-8">
                  <p className="text-sm text-gray-700">"Assign Team Alpha to the Johnson property job tomorrow"</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mr-8">
                  <p className="text-sm text-gray-700">I'll assign Team Alpha to the Johnson property job:</p>
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <p className="text-sm font-medium">456 Maple Avenue</p>
                    <p className="text-sm text-gray-500">Scheduled: Tomorrow, 9:00 AM</p>
                    <p className="text-sm text-purple-600">Crew: Team Alpha</p>
                  </div>
                  <p className="text-sm text-gray-700 mt-3">Confirm this assignment?</p>
                </div>
                <div className="flex gap-2 justify-end">
                  <span className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg">Yes, assign</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Get Your Crews Organized
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start managing your workforce more effectively with Orchard LMS.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-white text-primary hover:bg-gray-100">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default CrewManagementPage;
